import React, { useState, useEffect, useRef } from "react";
import {
  Card,
  CardBody,
  Card as CardMaterial,
  CardHeader,
} from "../../../../_metronic/_partials/controls";
import {
  Button,
  TextField,
  MuiThemeProvider,
  createMuiTheme,
  FormControlLabel,
  Checkbox,
  CardMedia,
  Tooltip,
  Radio,
  RadioGroup,
  FormControl,
  FormLabel,
} from "@material-ui/core";
import { useHistory, useParams } from "react-router-dom";
import {
  deleteUser,
  getUserById,
  postUser,
  updateUser,
} from "../../../../api/user";
import { useSkeleton } from "../../../hooks/useSkeleton";
import { alertError, alertSuccess } from "../../../../utils/logger";
import ConfirmDialog from "../../../components/dialogs/ConfirmDialog";
import VincularUsuarioDocenteDialog from "../../../components/dialogs/VincularUsuarioDocenteDialog";
import { SERVER_URL } from "../../../../api";
import { buttonsStyle } from "../../../components/tables/table";
import { Delete, Visibility } from "@material-ui/icons";
import PreviewDialog from "../../../components/dialogs/PreviewDialog";
import ArrowBackIcon from "@material-ui/icons/ArrowBack";

// Create theme for delete button (red)
const theme = createMuiTheme({
  palette: {
    secondary: {
      main: "#F64E60",
    },
  },
});

function getEmptyUser() {
  return {
    fullName: "",
    apellido1: "",
    apellido2: "",
    rrss: "",
    dni: "",
    email: "",
    imageURL: "",
    role: "docente",
    esDocente: true,
    active: true,
  };
}

export default function EditUserDocentePage() {
  const [user, setUser] = useState(getEmptyUser());
  const [openConfirmDialog, setOpenConfirmDialog] = useState(false);
  const imageInputRef = useRef(null);
  const [selectedImage, setSelectedImage] = useState(null);
  const [openPreviewDialog, setOpenPreviewDialog] = useState(null);
  const userId = useParams().id;
  const history = useHistory();

  // Modo creación: "new" = crear nuevo docente, "link" = vincular usuario existente
  const [createMode, setCreateMode] = useState("new");
  const [existingUserId, setExistingUserId] = useState(null);
  const [openVincularModal, setOpenVincularModal] = useState(false);

  const {
    isLoading: isLoadingData,
    disableLoading: disableLoadingData,
    ContentSkeleton,
  } = useSkeleton();

  useEffect(() => {
    const fetchData = async () => {
      try {
        if (!userId) {
          disableLoadingData();
          return;
        }
        getUserById(userId)
          .then((res) => {
            if (res.status === 200) {
              const user = res.data;
              setUser(user);

              disableLoadingData();
            }
          })
          .catch((error) => {
            alertError({
              error: error,
              customMessage: "Could not get users.",
            });
            history.push("/docentes");
          });
      } catch (error) {}
    };
    fetchData();
  }, [userId, disableLoadingData, history]);

  function saveUser() {
    let saveUser = { ...user };

    if (!userId) {
      // Vincular usuario existente: enviar existingUserId y el backend actualiza ese usuario
      if (existingUserId) {
        saveUser.existingUserId = existingUserId;
      }
      postUser(saveUser, selectedImage)
        .then((res) => {
          if (res.status === 201 || res.status === 200) {
            alertSuccess({
              title: "Saved!",
              customMessage: existingUserId
                ? "Usuario vinculado como docente correctamente."
                : "Docente successfully created.",
            });
            history.push("/docentes");
          }
        })
        .catch((error) => {
          const msg =
            error?.response?.data?.message || "Could not save docente.";
          alertError({
            error: error,
            customMessage: msg,
          });
        });
    } else {
      updateUser(userId, saveUser, selectedImage)
        .then((res) => {
          if (res.status === 200) {
            alertSuccess({
              title: "Saved!",
              customMessage: "Changes successfully saved.",
            });
            history.push("/docentes");
          }
        })
        .catch((error) => {
          alertError({
            error: error,
            customMessage: "Could not save changes.",
          });
        });
    }
  }

  const renderStickyButtons = ({
    onBack,
    onSave,
    onDelete,
    setObj,
    obj,
    showDelete = false,
  }) => {
    return (
      <div
        className="sticky-top bg-white border-top shadow-sm"
        style={{
          zIndex: 1000,
          paddingLeft: "15px",
          paddingRight: "15px",
        }}
      >
        <div className="container-fluid">
          <div className="row py-3">
            <div className="col-12">
              <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
                {/* Botón Back */}
                <div className="d-flex gap-2">
                  <Button
                    onClick={onBack}
                    variant="outlined"
                    size="medium"
                    className="text-nowrap"
                    startIcon={<ArrowBackIcon />}
                  >
                    Back
                  </Button>
                </div>

                {/* Botones Save y Delete */}
                <div className="d-flex gap-2">
                  <Button
                    onClick={onSave}
                    variant="outlined"
                    color="primary"
                    style={{ marginRight: "20px" }}
                  >
                    Save
                  </Button>

                  {showDelete && (
                    <MuiThemeProvider theme={theme}>
                      <Button
                        onClick={onDelete}
                        variant="outlined"
                        color="secondary"
                        size="medium"
                        className="text-nowrap"
                      >
                        Delete
                      </Button>
                    </MuiThemeProvider>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const handleChange = (element) => (event) => {
    setUser({ ...user, [element]: event.target.value });
  };

  if (isLoadingData) return <ContentSkeleton />;
  else
    return (
      <>
        <Card>
          {renderStickyButtons({
            onBack: () => history.push("/docentes"),
            onSave: saveUser,
            onDelete: () => setOpenConfirmDialog(true),
            showDelete: !!userId,
            setObj: setUser,
            obj: user,
          })}
          <CardHeader title={userId ? "Edit docente" : "Añadir docente"}></CardHeader>
          <CardBody>
            {!userId && (
              <FormControl component="fieldset" style={{ marginBottom: "20px" }}>
                <FormLabel component="legend">Tipo de alta</FormLabel>
                <RadioGroup
                  row
                  value={createMode}
                  onChange={(e) => {
                    const mode = e.target.value;
                    setCreateMode(mode);
                    if (mode === "new") {
                      setUser(getEmptyUser());
                      setExistingUserId(null);
                      setSelectedImage(null);
                    } else {
                      setExistingUserId(null);
                      setUser(getEmptyUser());
                    }
                  }}
                >
                  <FormControlLabel value="new" control={<Radio />} label="Crear nuevo docente" />
                  <FormControlLabel value="link" control={<Radio />} label="Vincular usuario existente como docente" />
                </RadioGroup>
              </FormControl>
            )}

            {!userId && createMode === "link" && (
              <div style={{ marginBottom: "20px" }}>
                <Button
                  variant="outlined"
                  color="primary"
                  onClick={() => setOpenVincularModal(true)}
                  style={{ marginRight: 8 }}
                >
                  Buscar usuario para vincular...
                </Button>
                {existingUserId && (
                  <p style={{ marginTop: 8, color: "#666" }}>
                    Usuario seleccionado: <strong>{user.fullName} {user.apellido1} {user.apellido2}</strong>
                    {user.dni ? ` (${user.dni})` : ""}. Se actualizará como docente. Puedes completar o modificar los campos debajo.
                  </p>
                )}
                <VincularUsuarioDocenteDialog
                  open={openVincularModal}
                  onClose={() => setOpenVincularModal(false)}
                  onSelect={(selectedUser) => {
                    setExistingUserId(selectedUser._id);
                    setUser({
                      fullName: selectedUser.fullName || "",
                      apellido1: selectedUser.apellido1 || "",
                      apellido2: selectedUser.apellido2 || "",
                      rrss: selectedUser.rrss || "",
                      dni: selectedUser.dni || "",
                      email: selectedUser.email || "",
                      imageURL: selectedUser.imageURL || "",
                      role: selectedUser.role || "user",
                      esDocente: true,
                      active: selectedUser.active !== false,
                    });
                    setSelectedImage(null);
                    setOpenVincularModal(false);
                  }}
                />
              </div>
            )}

            <div className="d-flex">
              <div className="col-md-4">
                <CardMaterial>
                  {(selectedImage || user.imageURL) && (
                    <CardMedia
                      component="img"
                      alt="New"
                      style={{
                        objectFit: "contain",
                        width: "100%",
                        height: "auto",
                        maxHeight: "600px",
                      }}
                      image={
                        selectedImage
                          ? URL.createObjectURL(selectedImage)
                          : `${SERVER_URL}/${user.imageURL}`
                      }
                    />
                  )}
                </CardMaterial>
                <label htmlFor={"upload-media"} className="ml-0 my-4">
                  <input
                    ref={imageInputRef}
                    style={{ display: "none" }}
                    id={"upload-media"}
                    name={"upload-media"}
                    type="file"
                    accept={"image/*"}
                    onChange={(e) => {
                      setSelectedImage(e.target.files[0]);
                    }}
                  />
                  <Button
                    style={{ marginRight: "15px" }}
                    color="secondary"
                    component="span"
                    variant="outlined"
                  >
                    {selectedImage || (user.imageURL && user.imageURL !== "")
                      ? "cambiar imagen"
                      : "subir imagen"}
                  </Button>
                </label>
                {(selectedImage || (user.imageURL && user.imageURL !== "")) && (
                  <>
                    <Tooltip title={"Preview"}>
                      <Button
                        size="small"
                        onClick={() => setOpenPreviewDialog(1)}
                        style={{
                          ...buttonsStyle,
                          marginRight: "15px",
                        }}
                      >
                        <Visibility />
                      </Button>
                    </Tooltip>
                    <PreviewDialog
                      title={"Preview"}
                      open={openPreviewDialog === 1}
                      setOpen={setOpenPreviewDialog}
                      file={selectedImage}
                      src={
                        selectedImage
                          ? URL.createObjectURL(selectedImage)
                          : `${SERVER_URL}/${user.imageURL}`
                      }
                    />
                    <Tooltip title={"Delete"}>
                      <Button
                        size="small"
                        onClick={() => {
                          setSelectedImage(null);
                          setUser({
                            ...user,
                            imageURL: null,
                          });
                          if (imageInputRef.current) {
                            imageInputRef.current.value = "";
                          }
                        }}
                      >
                        <Delete />
                      </Button>
                    </Tooltip>
                  </>
                )}
              </div>
              <div className="col-8">
                <TextField
                  id={`fullName`}
                  label="Nombre"
                  value={user.fullName}
                  onChange={handleChange("fullName")}
                  InputLabelProps={{
                    shrink: true,
                  }}
                  margin="normal"
                  variant="outlined"
                  required
                />
                <TextField
                  id={`apellido1`}
                  label="Apellido 1"
                  value={user.apellido1}
                  onChange={handleChange("apellido1")}
                  InputLabelProps={{
                    shrink: true,
                  }}
                  margin="normal"
                  variant="outlined"
                  required
                />
                <TextField
                  id={`apellido2`}
                  label="Apellido 2"
                  value={user.apellido2}
                  onChange={handleChange("apellido2")}
                  InputLabelProps={{
                    shrink: true,
                  }}
                  margin="normal"
                  variant="outlined"
                  required
                />
                <TextField
                  id={`dni`}
                  label="DNI"
                  value={user.dni}
                  onChange={handleChange("dni")}
                  InputLabelProps={{
                    shrink: true,
                  }}
                  margin="normal"
                  variant="outlined"
                  required
                />
                <TextField
                  id={`email`}
                  type="email"
                  label="Email"
                  value={user.email}
                  onChange={handleChange("email")}
                  InputLabelProps={{
                    shrink: true,
                  }}
                  margin="normal"
                  variant="outlined"
                  required
                />
                <TextField
                  id={`rrss`}
                  label="Redes Sociales"
                  value={user.rrss}
                  onChange={handleChange("rrss")}
                  InputLabelProps={{
                    shrink: true,
                  }}
                  margin="normal"
                  variant="outlined"
                  required
                />
              </div>
            </div>

            {/* Checkboxes */}
            <div style={{ marginTop: "20px" }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={user.active || false}
                    onChange={() =>
                      setUser({ ...user, active: !user.active })
                    }
                    name="checkActive"
                  />
                }
                label="Active"
              />
            </div>
          </CardBody>
        </Card>
        {userId && (
          <>
            <ConfirmDialog
              title={"Are you sure you want to delete this docente?"}
              open={openConfirmDialog}
              setOpen={setOpenConfirmDialog}
              onConfirm={() => {
                deleteUser(userId)
                  .then((res) => {
                    if (res.status === 204 || res.status === 200) {
                      alertSuccess({
                        title: "Deleted!",
                        customMessage: "Docente deleted successfully",
                      });
                      history.push("/docentes");
                    }
                  })
                  .catch((error) => {
                    alertError({
                      error: error,
                      customMessage: "Could not delete docente.",
                    });
                  });
              }}
            />
          </>
        )}
      </>
    );
}
